/**
 * One lead, and every conversation behind them.
 *
 * A slide-over rather than a route because the useful move from here is almost
 * always back to the list — you are scanning leads, not reading one. The link
 * into Live Chats is the exception, and it navigates properly.
 */
import { Link } from '@tanstack/react-router';
import { ExternalLink, Loader2, UserPlus, X } from 'lucide-react';
import { Badge, Button, cn } from '@respectly/ui';
import type { ChatLeadDto } from '@respectly/shared-types';
import { useChatLeadsStore } from '../../stores/useChatLeadsStore';
import { relativeTime } from '../live-chats/SessionList';

export function LeadDetailDrawer({
  onAddToContacts,
}: {
  onAddToContacts: (lead: ChatLeadDto) => void;
}) {
  const detail = useChatLeadsStore((s) => s.detail);
  const detailId = useChatLeadsStore((s) => s.detailId);
  const loading = useChatLeadsStore((s) => s.loadingDetail);
  const openLead = useChatLeadsStore((s) => s.openLead);

  if (!detailId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        className="flex-1 bg-slate-900/20 backdrop-blur-[1px]"
        onClick={() => void openLead(null)}
      />
      <aside className="w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Lead</h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => void openLead(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading || !detail ? (
          <p className="flex items-center gap-2 px-5 py-10 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading&hellip;
          </p>
        ) : (
          <div className="space-y-6 px-5 py-4">
            <section>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {detail.lead.name ?? detail.lead.email}
              </h3>
              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                <a
                  href={`mailto:${detail.lead.email}`}
                  className="text-teal-700 hover:underline dark:text-teal-400"
                >
                  {detail.lead.email}
                </a>
                {detail.lead.phone && ` · ${detail.lead.phone}`}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-[10px]">
                  {detail.lead.brandName}
                </Badge>
                <Badge variant="secondary" className="text-[10px]">
                  {detail.lead.sessionCount}{' '}
                  {detail.lead.sessionCount === 1 ? 'conversation' : 'conversations'}
                </Badge>
                {detail.lead.askedForPerson && (
                  <Badge variant="secondary" className="text-[10px]">
                    asked for a person
                  </Badge>
                )}
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                First seen {relativeTime(detail.lead.firstSeenAt)} · last seen{' '}
                {relativeTime(detail.lead.lastSeenAt)}
              </p>

              <Button
                size="sm"
                className="mt-3 h-8 gap-1.5"
                onClick={() => onAddToContacts(detail.lead)}
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add to contacts
              </Button>
            </section>

            {detail.sessions.length > 0 && (
              <section>
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Conversations
                </h4>
                <ul className="mt-2 space-y-1.5">
                  {detail.sessions.map((s) => (
                    <li key={s.id}>
                      <Link
                        to="/live-chats"
                        search={{ session: s.id }}
                        onClick={() => void openLead(null)}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs',
                          'hover:border-teal-400 hover:bg-teal-50/50',
                          'dark:border-slate-700 dark:hover:border-teal-600 dark:hover:bg-teal-950/20'
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-slate-700 dark:text-slate-200">
                            {s.preview ?? 'No messages'}
                          </span>
                          <span className="mt-0.5 block text-slate-400">
                            {s.messageCount} message{s.messageCount === 1 ? '' : 's'} ·{' '}
                            {relativeTime(s.lastActivityAt)}
                          </span>
                        </span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {detail.escalations.length > 0 && (
              <section>
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Requests for a person
                </h4>
                <ul className="mt-2 space-y-2">
                  {detail.escalations.map((e) => (
                    <li
                      key={e.id}
                      className="rounded-lg border border-slate-200 p-2.5 text-xs dark:border-slate-700"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-700 dark:text-slate-200">
                          {e.reason.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {relativeTime(e.createdAt)}
                        </span>
                      </div>
                      {e.summary && (
                        <p className="mt-1 text-slate-500 dark:text-slate-400">{e.summary}</p>
                      )}
                      <p className="mt-1 text-[10px] text-slate-400">
                        {e.status}
                        {e.assignedToName ? ` · ${e.assignedToName}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
